import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import { useAlert } from 'dashboard/composables';
// FORK_PATCH: ai-assist auto-trigger event
import { AI_ASSIST_INCOMING } from 'custom/dashboard/extensions/ai_agent/ai_assist/events';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'account.enrichment_completed': this.onEnrichmentCompleted,
      'copilot.message.created': this.onCopilotMessageCreated,
      // FORK_PATCH: agent AI reply assistant streaming events (custom ai_assist)
      'ai_assist.token': this.onAiAssistToken,
      'ai_assist.done': this.onAiAssistDone,
      // FORK_PATCH_BEGIN: billing-subscription — admin plan/credit reminder toast
      'billing.reminder': this.onBillingReminder,
      // FORK_PATCH_END: billing-subscription
      // FORK_PATCH_BEGIN: conversation-counsel
      'counsel.message_created': this.onCounselMessageCreated,
      'counsel.message_deleted': this.onCounselMessageDeleted,
      'counsel.thread_cleared': this.onCounselThreadCleared,
      'counsel.auto_reply_changed': this.onCounselAutoReplyChanged,
      'conversation.agent_activity': this.onConversationAgentActivity,
      // FORK_PATCH_END: conversation-counsel
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    // FORK_PATCH_BEGIN: account-public-id
    return (
      this.app.$store.getters.getCurrentAccountPublicId ===
      data.account_public_id
    );
    // FORK_PATCH_END: account-public-id
  };

  // FORK_PATCH_BEGIN: agent AI reply assistant — forward streamed draft chunks to
  // the custom ai_assist Vuex module (registered by the ai_agent extension).
  onAiAssistToken = data => {
    this.app.$store.dispatch('aiAssistAppendToken', {
      conversationId: data.conversation_id,
      requestId: data.request_id,
      content: data.content,
    });
  };

  onAiAssistDone = data => {
    this.app.$store.dispatch('aiAssistFinish', {
      conversationId: data.conversation_id,
      requestId: data.request_id,
      status: data.status,
    });
  };
  // FORK_PATCH_END: agent AI reply assistant

  // FORK_PATCH_BEGIN: conversation-counsel — feed the internal counsel panel
  // (custom counsel Vuex module, registered by the counsel extension).
  onCounselMessageCreated = data => {
    this.app.$store.dispatch('counselMessageReceived', {
      conversationId: data.conversation_id,
      message: data.message,
    });
  };

  onCounselMessageDeleted = data => {
    this.app.$store.dispatch('counselMessageDeleted', {
      conversationId: data.conversation_id,
      messageId: data.message_id,
    });
  };

  onCounselThreadCleared = data => {
    this.app.$store.dispatch('counselThreadCleared', {
      conversationId: data.conversation_id,
    });
  };

  onCounselAutoReplyChanged = data => {
    this.app.$store.dispatch('counselAutoReplyReceived', {
      conversationId: data.conversation_id,
      enabled: data.auto_reply_enabled,
    });
  };

  onConversationAgentActivity = data => {
    this.app.$store.dispatch('counselAgentActivityReceived', {
      conversationId: data.conversation_id,
      busy: data.busy,
      kind: data.kind,
      userId: data.user_id,
      requestId: data.request_id,
    });
  };
  // FORK_PATCH_END: conversation-counsel

  // FORK_PATCH_BEGIN: billing-subscription — surface billing reminders to admins
  // eslint-disable-next-line class-methods-use-this
  onBillingReminder = data => {
    const title = data?.title?.toString().trim();
    const body = data?.body?.toString().trim();
    if (!title && !body) return;
    useAlert([title, body].filter(Boolean).join(' — '));
  };
  // FORK_PATCH_END: billing-subscription

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
    // FORK_PATCH_BEGIN: voice-transcription — Assist after STT lands on message.updated
    if (
      (data.message_type === 0 || data.message_type === 'incoming') &&
      this.hasVoiceTranscript(data)
    ) {
      this.voiceAssistEmittedIds = this.voiceAssistEmittedIds || new Set();
      if (!this.voiceAssistEmittedIds.has(data.id)) {
        this.voiceAssistEmittedIds.add(data.id);
        emitter.emit(AI_ASSIST_INCOMING, {
          conversationId: data.conversation_id,
        });
      }
    }
    // FORK_PATCH_END: voice-transcription
  };

  // FORK_PATCH_BEGIN: voice-transcription — voice-only messages need STT before Assist
  // eslint-disable-next-line class-methods-use-this
  needsVoiceTranscript = data => {
    const content = (data.content || '').trim();
    if (content) return false;
    const attachments = data.attachments || [];
    const audio = attachments.find(
      a => a.file_type === 'audio' || a.fileType === 'audio'
    );
    if (!audio) return false;
    const text = (audio.transcribed_text || audio.transcribedText || '').trim();
    return !text;
  };

  // eslint-disable-next-line class-methods-use-this
  hasVoiceTranscript = data => {
    const attachments = data.attachments || [];
    return attachments.some(a => {
      const text = (a.transcribed_text || a.transcribedText || '').trim();
      return !!text;
    });
  };
  // FORK_PATCH_END: voice-transcription

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
    // FORK_PATCH: ai-assist — let ReplyBox decide whether to auto-draft a reply
    // when a customer (incoming) message arrives. message_type 0 = incoming.
    // FORK_PATCH_BEGIN: voice-transcription — wait for STT before Assist on voice-only
    if (data.message_type === 0 || data.message_type === 'incoming') {
      if (this.needsVoiceTranscript(data)) {
        return;
      }
      emitter.emit(AI_ASSIST_INCOMING, { conversationId });
    }
    // FORK_PATCH_END: voice-transcription
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onEnrichmentCompleted = () => {
    this.app.$store.dispatch('accounts/get', { silent: true });
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
